import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/base/Button';
import Input from '../../components/base/Input';
import { supabase } from '../../lib/supabase';

export default function ChangePasswordPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const validatePassword = (password: string) => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    if (password.length < minLength) {
      return 'A senha deve ter pelo menos 8 caracteres';
    }
    if (!hasUpperCase) {
      return 'A senha deve conter pelo menos uma letra maiúscula';
    }
    if (!hasLowerCase) {
      return 'A senha deve conter pelo menos uma letra minúscula';
    }
    if (!hasNumbers) {
      return 'A senha deve conter pelo menos um número';
    }
    if (!hasSpecialChar) {
      return 'A senha deve conter pelo menos um caractere especial';
    }
    return '';
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Limpar erro do campo quando o usuário começar a digitar
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
    
    // Limpar mensagem de sucesso
    if (successMessage) {
      setSuccessMessage('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    setIsLoading(true);

    // Validações
    const newErrors: {[key: string]: string} = {};

    if (!formData.currentPassword) {
      newErrors.currentPassword = 'Senha atual é obrigatória';
    }

    if (!formData.newPassword) {
      newErrors.newPassword = 'Nova senha é obrigatória';
    } else {
      const passwordError = validatePassword(formData.newPassword);
      if (passwordError) {
        newErrors.newPassword = passwordError;
      }
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Confirmação de senha é obrigatória';
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'As senhas não coincidem';
    }

    if (formData.currentPassword === formData.newPassword) {
      newErrors.newPassword = 'A nova senha deve ser diferente da senha atual';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setIsLoading(false);
      return;
    }

    // Alterar senha no Supabase Auth
    try {
      console.log('🔐 [ChangePassword] Iniciando alteração de senha...');
      
      // 1. Primeiro, verificar se a senha atual está correta tentando fazer re-autenticação
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user || !user.email) {
        setErrors({ general: 'Usuário não autenticado' });
        setIsLoading(false);
        return;
      }

      // Tentar fazer login com a senha atual para validá-la
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: formData.currentPassword
      });

      if (signInError) {
        console.error('❌ [ChangePassword] Senha atual incorreta:', signInError);
        setErrors({ currentPassword: 'Senha atual incorreta' });
        setIsLoading(false);
        return;
      }

      console.log('✅ [ChangePassword] Senha atual validada');

      // 2. Atualizar para a nova senha
      const { error: updateError } = await supabase.auth.updateUser({
        password: formData.newPassword
      });

      if (updateError) {
        console.error('❌ [ChangePassword] Erro ao atualizar senha:', updateError);
        setErrors({ general: `Erro ao alterar senha: ${updateError.message}` });
        setIsLoading(false);
        return;
      }

      console.log('✅ [ChangePassword] Senha alterada com sucesso!');
      
      // Sucesso
      setSuccessMessage('Senha alterada com sucesso!');
      setFormData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      
      // Redirecionar após 2 segundos
      setTimeout(() => {
        navigate('/profile');
      }, 2000);
      
    } catch (error) {
      console.error('❌ [ChangePassword] Erro inesperado:', error);
      setErrors({ general: 'Erro ao alterar senha. Tente novamente.' });
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = (field: 'current' | 'new' | 'confirm') => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const getPasswordStrength = (password: string) => {
    if (!password) return { strength: 0, text: '', color: '' };
    
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) strength++;

    const levels = [
      { text: 'Muito fraca', color: 'text-red-500' },
      { text: 'Fraca', color: 'text-orange-500' },
      { text: 'Regular', color: 'text-yellow-500' },
      { text: 'Boa', color: 'text-blue-500' },
      { text: 'Forte', color: 'text-green-500' }
    ];

    return { strength, ...levels[strength - 1] || levels[0] };
  };

  const passwordStrength = getPasswordStrength(formData.newPassword);

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-blue-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-100">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/profile')}
              className="w-10 h-10 bg-gray-100 rounded-2xl flex items-center justify-center hover:bg-gray-200 transition-colors"
            >
              <i className="ri-arrow-left-line text-gray-600 text-xl"></i>
            </button>
            
            <div className="text-center">
              <h1 className="text-xl font-bold text-gray-800">Alterar Senha</h1>
              <p className="text-sm text-gray-600">Altere sua senha de acesso</p>
            </div>
            
            <div className="w-10 h-10 bg-red-100 rounded-2xl flex items-center justify-center">
              <i className="ri-lock-line text-red-600 text-xl"></i>
            </div>
          </div>
        </div>
      </div>

      {/* Conteúdo */}
      <div className="px-6 py-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Mensagem de sucesso */}
          {successMessage && (
            <div className="bg-green-50 border border-green-200 rounded-2xl p-4">
              <div className="flex items-center">
                <i className="ri-check-circle-line text-green-600 text-xl mr-3"></i>
                <p className="text-green-800 font-medium">{successMessage}</p>
              </div>
            </div>
          )}

          {/* Erro geral */}
          {errors.general && (
            <div className="bg-red-50 border border-red-200 rounded-2xl p-4">
              <div className="flex items-center">
                <i className="ri-error-warning-line text-red-600 text-xl mr-3"></i>
                <p className="text-red-800 font-medium">{errors.general}</p>
              </div>
            </div>
          )}

          {/* Senha Atual */}
          <div className="bg-white rounded-3xl p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Senha Atual</h2>
            <div className="relative">
              <Input
                label="Senha atual"
                type={showPasswords.current ? 'text' : 'password'}
                value={formData.currentPassword}
                onChange={(e) => handleInputChange('currentPassword', e.target.value)}
                placeholder="Digite sua senha atual"
                error={errors.currentPassword}
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('current')}
                className="absolute right-3 top-9 text-gray-500 hover:text-gray-700"
              >
                <i className={showPasswords.current ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
              </button>
            </div>
          </div>

          {/* Nova Senha */}
          <div className="bg-white rounded-3xl p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Nova Senha</h2>
            <div className="space-y-4">
              <div className="relative">
                <Input
                  label="Nova senha"
                  type={showPasswords.new ? 'text' : 'password'}
                  value={formData.newPassword}
                  onChange={(e) => handleInputChange('newPassword', e.target.value)}
                  placeholder="Digite sua nova senha"
                  error={errors.newPassword}
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('new')}
                  className="absolute right-3 top-9 text-gray-500 hover:text-gray-700"
                >
                  <i className={showPasswords.new ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                </button>
              </div>

              {/* Indicador de força da senha */}
              {formData.newPassword && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Força da senha:</span>
                    <span className={`text-sm font-medium ${passwordStrength.color}`}>
                      {passwordStrength.text}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all duration-300 ${
                        passwordStrength.strength === 1 ? 'bg-red-500' :
                        passwordStrength.strength === 2 ? 'bg-orange-500' :
                        passwordStrength.strength === 3 ? 'bg-yellow-500' :
                        passwordStrength.strength === 4 ? 'bg-blue-500' :
                        'bg-green-500'
                      }`}
                      style={{ width: `${(passwordStrength.strength / 5) * 100}%` }}
                    ></div>
                  </div>
                </div>
              )}

              {/* Requisitos da senha */}
              <div className="bg-gray-50 rounded-2xl p-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Requisitos da senha:</p>
                <ul className="space-y-1 text-xs text-gray-600">
                  <li className={`flex items-center ${formData.newPassword.length >= 8 ? 'text-green-600' : ''}`}>
                    <i className={`mr-2 ${formData.newPassword.length >= 8 ? 'ri-check-line' : 'ri-close-line'}`}></i>
                    Pelo menos 8 caracteres
                  </li>
                  <li className={`flex items-center ${/[A-Z]/.test(formData.newPassword) ? 'text-green-600' : ''}`}>
                    <i className={`mr-2 ${/[A-Z]/.test(formData.newPassword) ? 'ri-check-line' : 'ri-close-line'}`}></i>
                    Uma letra maiúscula
                  </li>
                  <li className={`flex items-center ${/[a-z]/.test(formData.newPassword) ? 'text-green-600' : ''}`}>
                    <i className={`mr-2 ${/[a-z]/.test(formData.newPassword) ? 'ri-check-line' : 'ri-close-line'}`}></i>
                    Uma letra minúscula
                  </li>
                  <li className={`flex items-center ${/\d/.test(formData.newPassword) ? 'text-green-600' : ''}`}>
                    <i className={`mr-2 ${/\d/.test(formData.newPassword) ? 'ri-check-line' : 'ri-close-line'}`}></i>
                    Um número
                  </li>
                  <li className={`flex items-center ${/[!@#$%^&*(),.?":{}|<>]/.test(formData.newPassword) ? 'text-green-600' : ''}`}>
                    <i className={`mr-2 ${/[!@#$%^&*(),.?":{}|<>]/.test(formData.newPassword) ? 'ri-check-line' : 'ri-close-line'}`}></i>
                    Um caractere especial
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Confirmar Senha */}
          <div className="bg-white rounded-3xl p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Confirmar Nova Senha</h2>
            <div className="relative">
              <Input
                label="Confirmar nova senha"
                type={showPasswords.confirm ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                placeholder="Digite novamente sua nova senha"
                error={errors.confirmPassword}
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('confirm')}
                className="absolute right-3 top-9 text-gray-500 hover:text-gray-700"
              >
                <i className={showPasswords.confirm ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
              </button>
            </div>
            
            {/* Indicador de confirmação */}
            {formData.confirmPassword && (
              <div className="mt-3 flex items-center">
                {formData.newPassword === formData.confirmPassword ? (
                  <div className="flex items-center text-green-600">
                    <i className="ri-check-line mr-2"></i>
                    <span className="text-sm">Senhas coincidem</span>
                  </div>
                ) : (
                  <div className="flex items-center text-red-600">
                    <i className="ri-close-line mr-2"></i>
                    <span className="text-sm">Senhas não coincidem</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Botões */}
          <div className="flex space-x-4 pt-4">
            <Button
              type="button"
              onClick={() => navigate('/profile')}
              variant="outline"
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              className="flex-1"
              disabled={isLoading || !formData.currentPassword || !formData.newPassword || !formData.confirmPassword}
            >
              {isLoading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Alterando...
                </div>
              ) : (
                <>
                  <i className="ri-key-line mr-2"></i>
                  Alterar Senha
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}